import type { PerioMeasurement } from '@/utils/perio';
import { computePSRSextants, computePerioDiagnosis, PERIO_SITES } from '@/utils/perio';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExportPatient {
  id: string;
  full_name?: string | null;
  date_of_birth?: string | null;
}

interface ExportExam {
  examId: string;
  recordedAt: string;
  recordedByName?: string | null;
  measurements: PerioMeasurement[];
}

// ── HTML generator ────────────────────────────────────────────────────────────

function buildPrintHtml(exam: ExportExam, patient: ExportPatient): string {
  const psrScores = computePSRSextants(exam.measurements);
  const diagnosis = computePerioDiagnosis([
    { examId: exam.examId, recordedAt: exam.recordedAt, measurements: exam.measurements },
  ]);

  const date = new Date(exam.recordedAt).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // Build per-tooth rows
  const teethSet = new Set<string>();
  for (const m of exam.measurements) teethSet.add(m.tooth_fdi);
  const teeth = Array.from(teethSet).sort();

  const psrHtml = Object.entries(psrScores)
    .map(([key, score]) => {
      const bg = score === null ? '#e5e7eb' : score <= 0 ? '#d1fae5' : score === 1 ? '#fef9c3' : score <= 3 ? '#fed7aa' : '#fecaca';
      return `<td style="background:${bg};text-align:center;padding:6px 8px;border:1px solid #d1d5db;font-weight:bold;">${key.toUpperCase()}<br>${score ?? '–'}</td>`;
    })
    .join('');

  // Build measurement rows
  const measurementMap = new Map<string, PerioMeasurement>();
  for (const m of exam.measurements) {
    measurementMap.set(`${m.tooth_fdi}:${m.site}`, m);
  }

  const gridRows = teeth.map(fdi => {
    const siteCells = PERIO_SITES.map(site => {
      const m = measurementMap.get(`${fdi}:${site}`);
      const pd = m?.pocket_depth_mm;
      const rec = m?.recession_mm;
      const cal = m ? ((pd ?? 0) + (rec ?? 0)) : null;
      const bop = m?.bleeding_on_probing ? '●' : '○';
      const bg = pd == null ? '#f9fafb' : pd >= 6 ? '#fee2e2' : pd >= 4 ? '#fef9c3' : '#f0fdf4';
      return `<td style="background:${bg};text-align:center;padding:3px 4px;border:1px solid #e5e7eb;font-size:11px;">${pd ?? '–'}/${rec ?? '–'}/${cal ?? '–'} ${bop}</td>`;
    }).join('');
    return `<tr><td style="font-weight:bold;text-align:center;padding:3px 6px;border:1px solid #e5e7eb;">${fdi}</td>${siteCells}</tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="da">
<head>
<meta charset="UTF-8" />
<title>Parodontal Undersøgelse – ${patient.full_name ?? 'Patient'}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 20px 40px; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  h2 { font-size: 14px; color: #374151; margin-top: 20px; margin-bottom: 8px; border-bottom: 1px solid #d1d5db; padding-bottom: 4px; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
  th { background: #f3f4f6; text-align: center; padding: 6px 8px; border: 1px solid #d1d5db; font-size: 11px; }
  .meta-row { display: flex; gap: 24px; margin-bottom: 16px; }
  .meta-item { display: flex; flex-direction: column; }
  .meta-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
  .meta-value { font-size: 13px; font-weight: 600; }
  .disclaimer { margin-top: 16px; padding: 8px 12px; background: #fefce8; border: 1px solid #fbbf24; border-radius: 6px; font-size: 11px; color: #92400e; }
  .signature { margin-top: 40px; border-top: 1px solid #d1d5db; padding-top: 12px; color: #6b7280; font-size: 11px; }
  @media print {
    body { margin: 10mm 15mm; }
  }
</style>
</head>
<body>
  <h1>Parodontal Undersøgelse</h1>
  <div class="meta-row">
    <div class="meta-item">
      <span class="meta-label">Patient</span>
      <span class="meta-value">${patient.full_name ?? '–'}</span>
    </div>
    <div class="meta-item">
      <span class="meta-label">Dato</span>
      <span class="meta-value">${date}</span>
    </div>
    ${exam.recordedByName ? `<div class="meta-item"><span class="meta-label">Behandler</span><span class="meta-value">${exam.recordedByName}</span></div>` : ''}
  </div>

  <h2>PSR Sextantscores</h2>
  <table><tr>${psrHtml}</tr></table>

  <h2>Parodontal måletabel (PD/Rec/CAL BoP)</h2>
  <table>
    <thead>
      <tr>
        <th>Tand</th>
        ${PERIO_SITES.map(s => `<th>${s}</th>`).join('')}
      </tr>
    </thead>
    <tbody>${gridRows}</tbody>
  </table>

  <h2>Foreslået diagnose</h2>
  <p>
    ${diagnosis.stage ? `<strong>Stadie ${diagnosis.stage}</strong> · <strong>Grad ${diagnosis.grade ?? '?'}</strong><br>${diagnosis.summary}` : 'Ingen parodontale problemer registreret.'}
  </p>
  <div class="disclaimer">
    Algoritmisk forslag baseret på 2017 WWC + DSP. Klinisk vurdering kræves.
  </div>

  <div class="signature">
    Dato: ___________________________ &nbsp;&nbsp;&nbsp; Underskrift: ___________________________
  </div>
</body>
</html>`;
}

// ── Export function ───────────────────────────────────────────────────────────

/**
 * Opens a print-ready tab with the exam pre-rendered and triggers window.print().
 */
export function exportPerioPdf(exam: ExportExam, patient: ExportPatient): void {
  const html = buildPrintHtml(exam, patient);
  const win = window.open('', '_blank');
  if (!win) {
    alert('Pop-up blokeret. Tillad pop-ups for at eksportere PDF.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 500);
}

export type { ExportExam, ExportPatient };
